import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { NavLink, Link } from "react-router-dom";
import { BUSINESS } from "../../data/content";

const NAV = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Services", to: "/services" },
  { label: "Commercial", to: "/commercial" },
  { label: "Residential", to: "/residential" },
  { label: "Pricing", to: "/pricing" },
  { label: "Reviews", to: "/reviews" },
  { label: "Contact", to: "/contact" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
      className={`sticky top-0 z-50 transition-[background,backdrop-filter,box-shadow] duration-300 border-b border-fc-ink/10 ${
        scrolled ? "bg-white/90 backdrop-blur-xl shadow-[0_8px_30px_rgba(11,44,92,0.08)]" : "bg-white"
      }`}
    >
      <div className="mx-auto w-[92%] max-w-[1280px] flex items-center justify-between h-[76px]">
        <Link to="/" data-testid="brand-logo" className="flex items-center group">
          <img src="/fc-logo-trans.png" alt="F&C Air Conditioning" className="h-11 w-auto transition-transform duration-300 group-hover:scale-[1.03]" />
        </Link>

        <nav className="hidden lg:flex items-center gap-5">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `font-mono text-[11px] uppercase tracking-widest border-b-2 pb-1 transition-colors ${isActive ? "text-fc-navy border-fc-cobalt" : "text-fc-ink2 border-transparent hover:text-fc-navy"}`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="header-request-btn"
            className="hidden sm:inline-flex items-center gap-2 bg-fc-orange text-white px-6 py-3 text-sm font-bold hover:brightness-95 transition-all"
          >
            Request Service
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            className="lg:hidden h-10 w-10 grid place-items-center bg-fc-navy text-white rounded-full"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-t border-fc-ink/10"
          >
            <div className="mx-auto w-[92%] max-w-[1280px] py-6 flex flex-col gap-4">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                  className={({ isActive }) => `w-fit text-left font-display text-2xl tracking-tight border-b-2 pb-1 ${isActive ? "text-fc-navy border-fc-cobalt" : "text-fc-ink border-transparent"}`}
                >
                  {item.label}
                </NavLink>
              ))}
              <Link to="/contact" onClick={() => setOpen(false)} data-testid="mobile-nav-request" className="mt-2 inline-flex items-center gap-2 bg-fc-orange text-white px-5 py-3 text-sm font-bold w-fit">
                Request Service
              </Link>
              <a data-testid="mobile-nav-call" href={BUSINESS.phoneHref} className="inline-flex items-center gap-2 text-fc-navy font-semibold text-sm">
                <Phone size={15} /> Call {BUSINESS.phone}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
